import type { AppLocale, MessageTree } from "@airange/shared/i18n-common";

export const redblueActivityTopologyEvidencePageMessages: Record<AppLocale, MessageTree> = {
  "zh-CN": {
    redblue: {
      pages: {
        activityDetail: {
          topologyEvidence: {
            loadErrorTitle: "演练详情加载失败",
            retry: "重试",
            currentState: "当前运行状态",
            lifecycleTitle: "靶场生命周期证据",
            preparePhase: "部署阶段",
            releasePhase: "释放阶段",
            requestEvidence: "请求与投递证据",
            projectionEvidence: "运行投影证据",
            accessEndpoints: "访问入口",
            noRequest: "该阶段尚未发起请求。",
            noProjection: "该阶段尚未收到运行投影。",
            noAccessEndpoints: "当前投影明确报告 0 个访问入口。",
            accessEndpointsNotProvided: "该阶段投影不提供访问入口数据。",
            noProjectionErrors: "当前投影未报告错误。",
            notPublished: "尚未发布",
            noDeliveryError: "未报告投递错误",
            contractErrorTitle: "靶场生命周期证据契约错误",
            notReported: "未报告",
            fields: {
              requestId: "业务 request_id",
              traceId: "链路 trace_id",
              eventId: "事件 ID",
              outboxId: "Outbox ID",
              deliveryStatus: "投递状态",
              publishAttempts: "发布尝试次数",
              requestedAt: "请求时间",
              publishedAt: "发布时间",
              lastError: "最近投递错误",
              syncedAt: "投影同步时间",
              orchestrationRequestId: "编排 request_id",
              orchestrationStatus: "编排状态",
              resourceStatus: "资源状态",
              topologyInstanceId: "拓扑实例 ID",
              errorCode: "错误码",
              errorMessage: "错误信息",
              endpointKind: "入口类型",
              endpointNode: "节点",
              endpointAddress: "地址",
              endpointPurpose: "用途",
              endpointSource: "事实来源",
            },
            runtimeStatuses: {
              unprepared: "未部署",
              preparing: "部署中",
              prepared: "已部署",
              prepare_failed: "部署失败",
              releasing: "释放中",
              released: "已释放",
              release_failed: "释放失败",
            },
            deliveryStatuses: {
              pending: "待投递",
              publishing: "投递中",
              published: "已投递",
            },
            contractErrors: {
              current_status_mismatch: "拓扑绑定状态与生命周期证据状态不一致。",
              prepare_request_missing: "部署已开始，但部署请求证据缺失。",
              prepare_projection_missing: "部署已结束，但部署运行投影缺失。",
              release_request_missing: "释放已开始，但释放请求证据缺失。",
              release_projection_missing: "释放已结束，但释放运行投影缺失。",
              release_request_unexpected: "释放阶段尚未开始，却出现了释放请求证据。",
              release_projection_unexpected: "释放阶段尚未开始，却出现了释放运行投影。",
              prepare_request_id_mismatch: "部署请求与部署投影的 request_id 不一致。",
              release_request_id_mismatch: "释放请求与释放投影的 request_id 不一致。",
              prepare_projection_status_mismatch: "部署投影状态与当前生命周期阶段不一致。",
              release_projection_status_mismatch: "释放投影状态与当前生命周期阶段不一致。",
              topology_instance_id_mismatch: "拓扑绑定与运行投影的实例 ID 不一致。",
              topology_instance_id_missing: "已部署靶场缺少拓扑实例 ID 证据。",
              prepare_error_evidence_incomplete: "部署投影的错误码与错误信息没有成对提供。",
              release_error_evidence_incomplete: "释放投影的错误码与错误信息没有成对提供。",
              prepare_failure_evidence_missing: "部署失败投影缺少错误码和错误信息。",
              release_failure_evidence_missing: "释放失败投影缺少错误码和错误信息。",
              prepare_error_evidence_unexpected: "非失败部署投影不应携带错误证据。",
              release_error_evidence_unexpected: "非失败释放投影不应携带错误证据。",
            },
          },
        },
      },
    },
  },
  en: {
    redblue: {
      pages: {
        activityDetail: {
          topologyEvidence: {
            loadErrorTitle: "Failed to load activity details",
            retry: "Retry",
            currentState: "Current Runtime State",
            lifecycleTitle: "Range Lifecycle Evidence",
            preparePhase: "Deployment Phase",
            releasePhase: "Release Phase",
            requestEvidence: "Request and Delivery Evidence",
            projectionEvidence: "Runtime Projection Evidence",
            accessEndpoints: "Access Endpoints",
            noRequest: "No request has been issued for this phase.",
            noProjection: "No runtime projection has been received for this phase.",
            noAccessEndpoints: "The current projection explicitly reports zero access endpoints.",
            accessEndpointsNotProvided:
              "The projection does not provide access endpoint data for this phase.",
            noProjectionErrors: "The current projection reports no error.",
            notPublished: "Not published",
            noDeliveryError: "No delivery error reported",
            contractErrorTitle: "Range lifecycle evidence contract error",
            notReported: "Not reported",
            fields: {
              requestId: "Business request_id",
              traceId: "Trace trace_id",
              eventId: "Event ID",
              outboxId: "Outbox ID",
              deliveryStatus: "Delivery Status",
              publishAttempts: "Publish Attempts",
              requestedAt: "Requested At",
              publishedAt: "Published At",
              lastError: "Latest Delivery Error",
              syncedAt: "Projection Synced At",
              orchestrationRequestId: "Orchestration request_id",
              orchestrationStatus: "Orchestration Status",
              resourceStatus: "Resource Status",
              topologyInstanceId: "Topology Instance ID",
              errorCode: "Error Code",
              errorMessage: "Error Message",
              endpointKind: "Endpoint Kind",
              endpointNode: "Node",
              endpointAddress: "Address",
              endpointPurpose: "Purpose",
              endpointSource: "Evidence Source",
            },
            runtimeStatuses: {
              unprepared: "Not Deployed",
              preparing: "Deploying",
              prepared: "Deployed",
              prepare_failed: "Deployment Failed",
              releasing: "Releasing",
              released: "Released",
              release_failed: "Release Failed",
            },
            deliveryStatuses: {
              pending: "Pending",
              publishing: "Publishing",
              published: "Published",
            },
            contractErrors: {
              current_status_mismatch:
                "Topology binding status does not match lifecycle evidence status.",
              prepare_request_missing:
                "Deployment has started, but deployment request evidence is missing.",
              prepare_projection_missing:
                "Deployment has finished, but its runtime projection is missing.",
              release_request_missing:
                "Release has started, but release request evidence is missing.",
              release_projection_missing:
                "Release has finished, but its runtime projection is missing.",
              release_request_unexpected:
                "Release request evidence exists before the release phase starts.",
              release_projection_unexpected:
                "A release runtime projection exists before the release phase starts.",
              prepare_request_id_mismatch:
                "Deployment request and projection request_id values do not match.",
              release_request_id_mismatch:
                "Release request and projection request_id values do not match.",
              prepare_projection_status_mismatch:
                "Deployment projection status does not match the current lifecycle phase.",
              release_projection_status_mismatch:
                "Release projection status does not match the current lifecycle phase.",
              topology_instance_id_mismatch:
                "Topology binding and runtime projection instance IDs do not match.",
              topology_instance_id_missing:
                "A deployed range is missing topology instance ID evidence.",
              prepare_error_evidence_incomplete:
                "Deployment projection error code and message must be provided together.",
              release_error_evidence_incomplete:
                "Release projection error code and message must be provided together.",
              prepare_failure_evidence_missing:
                "A failed deployment projection is missing its error code and message.",
              release_failure_evidence_missing:
                "A failed release projection is missing its error code and message.",
              prepare_error_evidence_unexpected:
                "A non-failed deployment projection must not contain error evidence.",
              release_error_evidence_unexpected:
                "A non-failed release projection must not contain error evidence.",
            },
          },
        },
      },
    },
  },
  ja: {
    redblue: {
      pages: {
        activityDetail: {
          topologyEvidence: {
            loadErrorTitle: "演習詳細の読み込みに失敗しました",
            retry: "再試行",
            currentState: "現在の実行状態",
            lifecycleTitle: "演習環境ライフサイクル証跡",
            preparePhase: "デプロイ段階",
            releasePhase: "解放段階",
            requestEvidence: "リクエストと配信証跡",
            projectionEvidence: "実行投影証跡",
            accessEndpoints: "アクセス先",
            noRequest: "この段階のリクエストはまだ発行されていません。",
            noProjection: "この段階の実行投影はまだ受信されていません。",
            noAccessEndpoints: "現在の投影はアクセス先 0 件を明示しています。",
            accessEndpointsNotProvided: "この段階の投影にはアクセス先データがありません。",
            noProjectionErrors: "現在の投影にエラーは報告されていません。",
            notPublished: "未配信",
            noDeliveryError: "配信エラー報告なし",
            contractErrorTitle: "演習環境ライフサイクル証跡の契約エラー",
            notReported: "未報告",
            fields: {
              requestId: "業務 request_id",
              traceId: "追跡 trace_id",
              eventId: "イベント ID",
              outboxId: "Outbox ID",
              deliveryStatus: "配信状態",
              publishAttempts: "配信試行回数",
              requestedAt: "リクエスト日時",
              publishedAt: "配信日時",
              lastError: "直近の配信エラー",
              syncedAt: "投影同期日時",
              orchestrationRequestId: "オーケストレーション request_id",
              orchestrationStatus: "オーケストレーション状態",
              resourceStatus: "リソース状態",
              topologyInstanceId: "トポロジーインスタンス ID",
              errorCode: "エラーコード",
              errorMessage: "エラーメッセージ",
              endpointKind: "アクセス種別",
              endpointNode: "ノード",
              endpointAddress: "アドレス",
              endpointPurpose: "用途",
              endpointSource: "事実ソース",
            },
            runtimeStatuses: {
              unprepared: "未デプロイ",
              preparing: "デプロイ中",
              prepared: "デプロイ済み",
              prepare_failed: "デプロイ失敗",
              releasing: "解放中",
              released: "解放済み",
              release_failed: "解放失敗",
            },
            deliveryStatuses: {
              pending: "配信待ち",
              publishing: "配信中",
              published: "配信済み",
            },
            contractErrors: {
              current_status_mismatch:
                "トポロジー紐付け状態とライフサイクル証跡状態が一致しません。",
              prepare_request_missing: "デプロイ開始後もデプロイリクエスト証跡がありません。",
              prepare_projection_missing: "デプロイ終了後も実行投影がありません。",
              release_request_missing: "解放開始後も解放リクエスト証跡がありません。",
              release_projection_missing: "解放終了後も実行投影がありません。",
              release_request_unexpected: "解放段階の開始前に解放リクエスト証跡が存在します。",
              release_projection_unexpected: "解放段階の開始前に解放実行投影が存在します。",
              prepare_request_id_mismatch: "デプロイリクエストと投影の request_id が一致しません。",
              release_request_id_mismatch: "解放リクエストと投影の request_id が一致しません。",
              prepare_projection_status_mismatch:
                "デプロイ投影状態が現在のライフサイクル段階と一致しません。",
              release_projection_status_mismatch:
                "解放投影状態が現在のライフサイクル段階と一致しません。",
              topology_instance_id_mismatch:
                "トポロジー紐付けと実行投影のインスタンス ID が一致しません。",
              topology_instance_id_missing:
                "デプロイ済み演習環境のトポロジーインスタンス ID 証跡がありません。",
              prepare_error_evidence_incomplete:
                "デプロイ投影のエラーコードとメッセージは同時に必要です。",
              release_error_evidence_incomplete:
                "解放投影のエラーコードとメッセージは同時に必要です。",
              prepare_failure_evidence_missing:
                "デプロイ失敗投影にエラーコードとメッセージがありません。",
              release_failure_evidence_missing:
                "解放失敗投影にエラーコードとメッセージがありません。",
              prepare_error_evidence_unexpected:
                "失敗していないデプロイ投影にエラー証跡を含めることはできません。",
              release_error_evidence_unexpected:
                "失敗していない解放投影にエラー証跡を含めることはできません。",
            },
          },
        },
      },
    },
  },
};
